import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { profilesService, supabaseAuth } from '../services/supabase';
import { isLoggingOutFlagSet } from '../utils/authReset';

export type Language = 'en' | 'fr';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string, params?: Record<string, string | number>) => string;
  /** Always French — use for public SEO meta (title, description, OG). UI stays bilingual. */
  tSeo: (key: string, params?: Record<string, string | number>) => string;
  isLanguageReady: boolean; // true when language is determined (either from profile or browser)
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Import translations dynamically
let enTranslations: any;
let frTranslations: any;

try {
  enTranslations = require('../locales/en').enTranslations;
  frTranslations = require('../locales/fr').frTranslations;
} catch (error) {
  console.error('Error loading translations:', error);
  // Fallback empty translations
  enTranslations = {};
  frTranslations = {};
}

// Translation data
const translations: Record<Language, any> = {
  en: enTranslations,
  fr: frTranslations,
};

// Translation function
const translate = (key: string, language: Language, params?: Record<string, string | number>): string => {
  const keys = key.split('.');
  let value: any = translations[language];
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Fallback to English if key not found in current language
      value = translations.en;
      for (const fallbackKey of keys) {
        if (value && typeof value === 'object' && fallbackKey in value) {
          value = value[fallbackKey];
        } else {
          return key; // Return key if not found in any language
        }
      }
      break;
    }
  }
  
  if (typeof value === 'string') {
    // Replace parameters in the translation string
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey]?.toString() || match;
      });
    }
    return value;
  }
  
  return key; // Return key if value is not a string
};

// Storage key for language preference (for unauthenticated users and faster initial load)
const LANGUAGE_STORAGE_KEY = 'leadsme:language';

// Get language from localStorage (if available)
const getStoredLanguage = (): Language | null => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'fr') {
      return stored;
    }
  } catch {
    // localStorage not available (SSR, private mode, etc.)
  }
  return null;
};

// Save language to localStorage
const saveLanguageToStorage = (lang: Language): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // localStorage not available - ignore
  }
};

// Detect browser language (used for unauthenticated users and new user creation).
// FR-first SaaS: if French appears anywhere in preferred languages → fr;
// else English or any other locale → en; unknown/crawler → fr.
export const detectBrowserLanguage = (): Language => {
  const checkEn = (lang: string) => Boolean(lang && lang.toLowerCase().startsWith('en'));
  const checkFr = (lang: string) => Boolean(lang && lang.toLowerCase().startsWith('fr'));

  const preferred = [
    ...(navigator.languages && navigator.languages.length > 0 ? navigator.languages : []),
    navigator.language || (navigator as any).userLanguage || '',
  ].filter(Boolean) as string[];

  if (preferred.some(checkFr)) return 'fr';
  if (preferred.some(checkEn)) return 'en';
  if (preferred.some((lang) => lang.length >= 2)) return 'en';
  // Truly unknown (crawler, no navigator): French product default
  return 'fr';
};

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Track the current user ID to detect user changes
  const currentUserIdRef = useRef<string | null>(null);
  // Track if we're currently loading profile to prevent race conditions
  const isLoadingProfileRef = useRef(false);

  // For authenticated users: source of truth is the database
  // For unauthenticated users: use stored language or browser language
  const [language, setLanguageState] = useState<Language>(() => {
    // Standard SaaS practice: Try localStorage first for faster initial load
    // This prevents flash of content in wrong language
    // NOTE: For authenticated users, DB will override this after profile loads
    const storedLang = getStoredLanguage();
    if (storedLang) {
      return storedLang;
    }
    // Fallback to browser language
    return detectBrowserLanguage();
  });
  
  // Track if we've determined the correct language
  // false = still checking if user is logged in and loading their preference
  // true = language is ready to use (either from profile or browser)
  // For unauthenticated users, we can set this to true immediately since we have the language
  const [isLanguageReady, setIsLanguageReady] = useState(false);

  // Listen to auth state changes and load language from profile when user changes
  useEffect(() => {
    let cancelled = false;

    // Load language from user profile
    // Standard SaaS: DB is source of truth for authenticated users, always override localStorage
    const loadLanguageFromProfile = async (userId: string): Promise<boolean> => {
      if (isLoadingProfileRef.current) {
        // Already loading, skip to avoid race conditions
        return false;
      }
      
      isLoadingProfileRef.current = true;
      
      try {
        // Timeout to avoid hanging (standard SaaS: fail fast)
        const profilePromise = profilesService.getProfile();
        const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000));
        const profile: any = await Promise.race([profilePromise as any, timeoutPromise]);

        if (cancelled) {
          isLoadingProfileRef.current = false;
          return false;
        }
        
        // Standard SaaS: DB always wins for authenticated users
        if (profile?.language && (profile.language === 'en' || profile.language === 'fr')) {
          const dbLanguage = profile.language;
          // Always update to match DB (even if same, to ensure sync)
          setLanguageState(dbLanguage);
          // Sync localStorage with DB (DB is source of truth)
          saveLanguageToStorage(dbLanguage);
          // Mark as ready once profile language is loaded
          if (!cancelled) {
            setIsLanguageReady(true);
          }
          isLoadingProfileRef.current = false;
          return true;
        }

        // No profile yet (created async on SIGNED_IN) — keep local preference, do not PATCH
        if (!profile) {
          const currentLang = getStoredLanguage() || detectBrowserLanguage();
          setLanguageState(currentLang);
          saveLanguageToStorage(currentLang);
          if (!cancelled) {
            setIsLanguageReady(true);
          }
          isLoadingProfileRef.current = false;
          return false;
        }
        
        // Profile exists but no language set - use current language and save to DB
        // This handles migration case where user has profile but no language preference
        const currentLang = getStoredLanguage() || detectBrowserLanguage();
        setLanguageState(currentLang);
        saveLanguageToStorage(currentLang);
        
        // Try to save to DB (best effort, don't block)
        try {
          await profilesService.updateProfile({ language: currentLang });
        } catch {
          // Silent fail - localStorage is already saved
        }
        
        if (!cancelled) {
          setIsLanguageReady(true);
        }
        isLoadingProfileRef.current = false;
        return false;
      } catch (error) {
        // Silent: keep current language from localStorage/browser
        // Mark as ready even on error (we have a language)
        if (!cancelled) {
          setIsLanguageReady(true);
        }
        isLoadingProfileRef.current = false;
        return false;
      }
    };

    // Helper to check if logout is in progress (avoid re-renders during hard navigation)
    const isLoggingOut = (): boolean => isLoggingOutFlagSet();

    // Subscribe BEFORE checkInitialSession. Keep sync — awaiting here deadlocks PKCE with AuthContext.
    const { data: { subscription } } = supabaseAuth.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (isLoggingOut()) return;

      const newUserId = session?.user?.id || null;
      const previousUserId = currentUserIdRef.current;

      if (newUserId === previousUserId) return;
      currentUserIdRef.current = newUserId;

      if (newUserId) {
        setTimeout(() => {
          void (async () => {
            if (cancelled || isLoggingOut()) return;
            const loaded = await loadLanguageFromProfile(newUserId);
            if (!loaded && !cancelled) {
              setIsLanguageReady(true);
            }
          })();
        }, 0);
        return;
      }

      isLoadingProfileRef.current = false;
      const storedLang = getStoredLanguage();
      const browserLang = storedLang || detectBrowserLanguage();
      setLanguageState((prev) => (prev === browserLang ? prev : browserLang));
      if (!cancelled) {
        setIsLanguageReady(true);
      }
    });

    // Check initial session on mount
    const checkInitialSession = async () => {
      try {
        const { safeGetSession } = await import('../services/supabase');
        const { data: { session } } = await safeGetSession(4000);
        if (cancelled) return;
        
        const userId = session?.user?.id || null;
        // Prefer auth listener if it already advanced the user id
        if (currentUserIdRef.current && currentUserIdRef.current !== userId) {
          return;
        }
        currentUserIdRef.current = userId;
        
        if (userId) {
          // User is logged in: load their language preference from database
          // loadLanguageFromProfile will set isLanguageReady when done
          await loadLanguageFromProfile(userId);
        } else {
          // User is not logged in: language is already set (from localStorage or browser)
          // Standard SaaS practice: Mark as ready immediately for public pages
          // This prevents unnecessary loading state on public routes
          if (!cancelled) {
            setIsLanguageReady(true);
          }
        }
        
      } catch (error) {
        // Silent: keep current language
        // If error occurs, still mark as ready (we have a language from localStorage/browser)
        if (!cancelled) {
          setIsLanguageReady(true);
        }
      }
    };

    checkInitialSession();

    // Failsafe: never block the app forever on language bootstrap
    const readyFailsafe = window.setTimeout(() => {
      if (!cancelled) setIsLanguageReady(true);
    }, 6000);

    // Standard SaaS: Listen for storage changes (multi-tab sync)
    // If language changes in another tab, update this tab
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && e.newValue) {
        if (e.newValue === 'en' || e.newValue === 'fr') {
          // Only update if user is not authenticated (for authenticated users, DB is source of truth)
          // Check current language via state setter callback to avoid stale closure
          try {
            const { safeGetSession } = await import('../services/supabase');
            const { data: { session } } = await safeGetSession(3000);
            if (!session?.user && !cancelled) {
              setLanguageState(prev => {
                // Only update if different to avoid unnecessary re-renders
                return prev !== e.newValue ? (e.newValue as Language) : prev;
              });
            }
          } catch {
            // If we can't check session, don't update (safer)
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      cancelled = true;
      isLoadingProfileRef.current = false;
      window.clearTimeout(readyFailsafe);
      subscription.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Empty deps: only run on mount/unmount

  const setLanguage = async (lang: Language) => {
    // Standard SaaS: Optimistic update - update UI immediately
    setLanguageState(lang);
    // Save to localStorage immediately for fast persistence
    // This ensures language persists even if database save fails
    saveLanguageToStorage(lang);

    try {
      // Save to database (source of truth for authenticated users)
      const { safeGetSession } = await import('../services/supabase');
      const { data: { session } } = await safeGetSession(4000);
      if (session?.user) {
        // Standard SaaS: If profile is currently loading, wait a bit to avoid race condition
        if (isLoadingProfileRef.current) {
          // Wait for profile load to complete (max 2.5s)
          let attempts = 0;
          while (isLoadingProfileRef.current && attempts < 25) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
          }
        }
        
        // Update DB (source of truth)
        await profilesService.updateProfile({ language: lang });
        // localStorage already saved above, no need to save again
      }
    } catch (error) {
      // Silently fail - UI already updated and localStorage is saved
      // User will see their preference even if DB save fails
    }
  };

  const t = (key: string, params?: Record<string, string | number>) => {
    return translate(key, language, params);
  };

  // Public SEO must stay French even if the UI language is English (same URL, no /en routes)
  const tSeo = (key: string, params?: Record<string, string | number>) => {
    return translate(key, 'fr', params);
  };

  // Keep <html lang> in sync with current language (SEO and a11y)
  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = language === 'fr' ? 'fr' : 'en';
    }
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, tSeo, isLanguageReady }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
